import { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { Plus, ListPlus } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Modal } from "antd";
import AddExamModal from "./AddExammodal";

const CoursesModal = ({ currentClass, onSubmit, isEdit }) => {
  const { register, control, handleSubmit, setValue, watch, reset } = useForm({
    defaultValues: {
      name: "",
      creditHours: "",
      startedSession: "",
      endSession: "",
      type: "Online",
      actualPrice: 0,
      discountedPrice: 0,
      description: "",
      weeks: [],
    },
  });
  const { i18n } = useTranslation();
  const isArabic = i18n.language;
  const [examModal, setExamModal] = useState(false);
  const [selectedWeekIndex, setSelectedWeekIndex] = useState(null);
  const [selectedActivityIndex, setSelectedActivityIndex] = useState(null);
  const [newPicture, setNewPicture] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [newVideo, setNewVideo] = useState(null);
  const [isForStudent, setIsForStudent] = useState(true); // Checkbox state
  const [activityFiles, setActivityFiles] = useState({}); // To track activity files

  const {
    fields: weekFields,
    append: appendWeek,
    remove: removeWeek,
  } = useFieldArray({
    control,
    name: "weeks",
  });

  useEffect(() => {
    if (currentClass) {
      console.log("Current class for edit:", currentClass);
      
      // Map plans to weeks with activities including files properly
      const weeksData = currentClass.plans?.map((plan) => ({
        id: plan.id || null, // Include plan ID for updates
        weekName: plan.weekName || "", 
        activities: plan.activities.map((activity) => ({
          id: activity.id || null, // Include activity ID for updates
          activityName: activity.activityName || "",
          activityType: activity.activityType || "",
          file: activity.file || activity.filePath || "", // Use either file or filePath property
          description: activity.activityDescription || "",
          // Make sure examDetails is properly structured for both editing and refreshing
          examDetails: activity.examDetails || activity.courseExam || null,
        })),
      })) || [];
      
      // Create a map of activity files for tracking
      const filesMap = {};
      if (currentClass.plans) {
        currentClass.plans.forEach((plan, weekIndex) => {
          plan.activities.forEach((activity, activityIndex) => {
            if ((activity.activityType === "Document" || activity.activityType === "Video") 
                && (activity.file || activity.filePath)) {
              const key = `${weekIndex}-${activityIndex}`;
              filesMap[key] = activity.file || activity.filePath;
            }
          });
        });
      }
      setActivityFiles(filesMap);
      
      reset({
        name: currentClass.name || "",
        creditHours: currentClass.creditHours || "",
        startedSession: currentClass.startedSession
          ? new Date(currentClass.startedSession).toISOString().split("T")[0]
          : "",
        endSession: currentClass.endSession
          ? new Date(currentClass.endSession).toISOString().split("T")[0]
          : "",
        type: currentClass.type || "Online",
        actualPrice: currentClass.actualPrice || 0,
        discountedPrice: currentClass.discountedPrice || 0,
        coverImageUrl: currentClass.coverImageUrl,
        videoUrl: currentClass.videoUrl,
        description: currentClass.description || "",
        weeks: weeksData,
      });
    }
  }, [currentClass, reset]);

  // Helper function to get file key
  const getFileKey = (weekIndex, activityIndex) => `${weekIndex}-${activityIndex}`;

  const handleCheckboxChange = () => {
    setIsForStudent((prev) => !prev);
    console.log(
      `Checkbox toggled: ${
        !isForStudent ? "For Student" : "For Teacher"
      } selected.`
    );
  };

  const handlePictureChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setNewPicture(URL.createObjectURL(file));
    }
  };

  const handleVideoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedVideo(file);
      setNewVideo(URL.createObjectURL(file));
    }
  };

  const addActivity = (weekIndex) => {
    // Get current activities or initialize empty array
    const activities = watch(`weeks.${weekIndex}.activities`) || [];
    
    // Add new activity with file field initialized to empty string
    setValue(`weeks.${weekIndex}.activities`, [
      ...activities,
      { activityName: "", activityType: "", file: "", description: "", examDetails: null },
    ]);
  };

  const removeActivity = (weekIndex, activityIndex) => {
    const activities = watch(`weeks.${weekIndex}.activities`);
    activities.splice(activityIndex, 1);
    setValue(`weeks.${weekIndex}.activities`, [...activities]);
    
    // Also remove from activityFiles if exists
    const key = getFileKey(weekIndex, activityIndex);
    if (activityFiles[key]) {
      const newActivityFiles = { ...activityFiles };
      delete newActivityFiles[key];
      setActivityFiles(newActivityFiles);
    }
  };

  const openExamModal = (weekIndex, activityIndex) => {
    // Store the indices to know where to update the exam data later
    setSelectedWeekIndex(weekIndex);
    setSelectedActivityIndex(activityIndex);
    setExamModal(true);
  };

  // Get the current exam data for the selected activity
  const getCurrentExamDetails = () => {
    if (selectedWeekIndex !== null && selectedActivityIndex !== null) {
      const activities = watch(`weeks.${selectedWeekIndex}.activities`) || [];
      if (activities[selectedActivityIndex]) {
        return activities[selectedActivityIndex].examDetails;
      }
    }
    return null;
  };

  const handleExamData = (examData) => {
    if (selectedWeekIndex !== null && selectedActivityIndex !== null) {
      const activities = watch(`weeks.${selectedWeekIndex}.activities`) || [];
      
      // Make sure the activities array exists
      if (!activities[selectedActivityIndex]) {
        console.error("Activity not found at index:", selectedActivityIndex);
        return;
      }
      
      // Update the exam details for the selected activity
      const updatedActivities = [...activities];
      updatedActivities[selectedActivityIndex] = {
        ...updatedActivities[selectedActivityIndex],
        examDetails: examData, // Use examDetails instead of courseExam
        // Ensure we maintain a valid file value (empty string) even for exam activities
        file: updatedActivities[selectedActivityIndex].file || ""
      };
      
      // Update the form state
      setValue(`weeks.${selectedWeekIndex}.activities`, updatedActivities);
      
      console.log("Exam data saved:", examData);
      console.log("Updated activities:", updatedActivities);
      
      // Reset the selection indices
      setSelectedWeekIndex(null);
      setSelectedActivityIndex(null);
    }
  };

  const formatDataForSubmission = (data) => {
    // Convert the form data to match the Swagger API format
    return {
      name: data.name,
      type: data.type,
      creditHours: Number(data.creditHours),
      startedSession: data.startedSession ? new Date(data.startedSession).toISOString() : null,
      endSession: data.endSession ? new Date(data.endSession).toISOString() : null,
      coverImageUrl: data.coverImageUrl || "",
      videoUrl: data.videoUrl || "",
      actualPrice: Number(data.actualPrice),
      discountedPrice: Number(data.discountedPrice),
      description: data.description,
      plans: data.weeks.map(week => ({
        id: isEdit && week.id ? week.id : undefined, // Include ID only for updates
        weekName: week.weekName,
        activities: week.activities.map(activity => ({
          id: isEdit && activity.id ? activity.id : undefined, // Include ID only for updates
          activityName: activity.activityName,
          activityType: activity.activityType,
          file: activity.file,
          activityDescription: activity.description, // Map to activityDescription if needed by API
          examDetails: activity.activityType === "Exam" ? {
            ...activity.examDetails,
            mcqs: activity.examDetails?.mcqs?.map(mcq => ({
              id: isEdit && mcq.id ? mcq.id : undefined, // Include ID only for updates
              question: mcq.question,
              optionA: mcq.optionA,
              optionB: mcq.optionB,
              optionC: mcq.optionC,
              optionD: mcq.optionD,
              marks: Number(mcq.marks),
              time: Number(mcq.time),
              correctOption: mcq.correctOption
            })) || [],
            questionAnswers: activity.examDetails?.questionAnswers?.map(qa => ({
              id: isEdit && qa.id ? qa.id : undefined, // Include ID only for updates
              question: qa.question,
              answer: qa.answer,
              marks: Number(qa.marks),
              time: Number(qa.time)
            })) || []
          } : null
        }))
      }))
    };
  };

  const onSubmitForm = (data) => {
    console.log("Submitting form data:", data);
    console.log(`Course is for: ${isForStudent ? "Student" : "Teacher"}`);
    
    // Ensure all activities have a file field, even if it's an empty string
    data.weeks.forEach((week) => {
      if (week.activities) {
        week.activities.forEach((activity) => {
          // Ensure file field is at least an empty string
          if (activity.file === null || activity.file === undefined) {
            activity.file = "";
          }
          
          // Make sure examDetails is kept if it exists
          if (activity.activityType === "Exam" && !activity.examDetails) {
            console.warn("Missing exam details for an Exam activity");
          }
        });
      }
    });
    
    // Format data to match API expectations
    const formattedData = formatDataForSubmission(data);
    
    toast.success("Form submitted successfully!");
    onSubmit(formattedData, selectedFile, selectedVideo);
  };
  
  return (
    <>
      <Toaster />
      <form onSubmit={handleSubmit(onSubmitForm)}>
        <div className="w-full px-4">
          <div className="flex justify-center py-2 font-bold text-lg">
            Course Information
          </div>
          <div className="flex items-center space-x-4 justify-center mb-4">
            <label className="block">Create Course For</label>
            <div className="flex items-center">
              <input
                type="checkbox"
                className="mr-2"
                checked={isForStudent}
                onChange={handleCheckboxChange}
              />
              <span>{isForStudent ? "Student" : "Teacher"}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block mb-2">Course Name</label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded p-2"
                placeholder="Course name"
                {...register("name")}
              />
            </div>
            <div>
              <label className="block mb-2">Course Type</label>
              <select
                className="w-full border border-gray-300 rounded p-2"
                {...register("type")}
              >
                <option>Online</option>
                <option>Physical</option>
                <option>Both</option>
              </select>
            </div>
            <div>
              <label className="block mb-2">Credit Hours</label>
              <input
                type="number"
                className="w-full border border-gray-300 rounded p-2"
                {...register("creditHours")}
              />
            </div>
            <div>
              <label className="block mb-2">Start Time</label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded p-2"
                {...register("startedSession")}
              />
            </div>
            <div>
              <label className="block mb-2">End Time</label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded p-2"
                {...register("endSession")}
              />
            </div>

            <div>
              <label className="block">Actual Price</label>
              <input
                type="number"
                className="w-full p-2 border rounded"
                {...register("actualPrice")}
              />
            </div>
            <div>
              <label className="block">Discounted Price</label>
              <input
                type="number"
                className="w-full p-2 border rounded"
                {...register("discountedPrice")}
              />
            </div>

            <div>
              <label className="block">Upload Cover Image</label>
              <input
                type="file"
                className="w-full p-2 border rounded"
                onChange={handlePictureChange}
                accept="image/*"
              />
              {newPicture && (
                <div className="mt-2">
                  <img
                    src={newPicture}
                    alt="Cover Image Preview"
                    className="w-20 h-20 rounded"
                  />
                </div>
              )}
              {currentClass && !newPicture && currentClass.coverImageUrl && (
                <div className="mt-2">
                  <img
                    src={currentClass.coverImageUrl}
                    alt="Existing Cover Image"
                    className="w-20 h-20 rounded"
                  />
                </div>
              )}
            </div>

            <div>
              <label className="block">Upload Video</label>
              <input
                type="file"
                className="w-full p-2 border rounded"
                onChange={handleVideoChange}
                accept="video/*"
              />
              {newVideo && (
                <div className="mt-2">
                  <video
                    src={newVideo}
                    alt="Video Preview"
                    className="w-20 h-20 rounded"
                    controls
                  />
                </div>
              )}
              {currentClass && !newVideo && currentClass.videoUrl && (
                <div className="mt-2">
                  <video
                    src={currentClass.videoUrl}
                    alt="Existing Video"
                    className="w-20 h-20 rounded"
                    controls
                  />
                </div>
              )}
            </div>

            <div>
              <label className="block">Course Description</label>
              <textarea
                type="text"
                placeholder="Course Description"
                className="w-full p-2 border rounded"
                {...register("description")}
              />
            </div>
          </div>

          <div className="w-full mt-4">
            <div className="flex justify-between items-center mb-2">
              <label className="font-bold text-lg">Plan Your Course</label>
              <button
                type="button"
                className="bg-gray-600 text-white py-1 px-4 rounded"
                onClick={() => appendWeek({ weekName: "", activities: [] })}
              >
                <div className="flex items-center">
                  <Plus className="mr-2" /> Add Week
                </div>
              </button>
            </div>
            <div className="space-y-4">
              {weekFields.map((week, weekIndex) => (
                <div key={week.id} className="border p-4 rounded-lg bg-gray-50">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      className="border border-gray-300 rounded p-2 flex-grow"
                      placeholder="Week Title"
                      {...register(`weeks.${weekIndex}.weekName`)}
                    />
                    <button
                      type="button"
                      className="bg-red-500 text-white rounded p-2"
                      onClick={() => removeWeek(weekIndex)}
                    >
                      &#x2715;
                    </button>
                    <button
                      type="button"
                      className="bg-[#241763] text-white rounded p-2 flex items-center"
                      onClick={() => addActivity(weekIndex)}
                    >
                      <ListPlus className="mr-1" size={16} /> Add Activity
                    </button>
                  </div>
                  {watch(`weeks.${weekIndex}.activities`)?.map(
                    (activity, activityIndex) => {
                      const activityType = watch(
                        `weeks.${weekIndex}.activities.${activityIndex}.activityType`
                      );
                      
                      // Get the file path from either the form or the activityFiles state
                      const filePath = watch(
                        `weeks.${weekIndex}.activities.${activityIndex}.file`
                      );
                      
                      const fileKey = getFileKey(weekIndex, activityIndex);
                      const existingFile = activityFiles[fileKey];
                      
                      const examDetails = watch(
                        `weeks.${weekIndex}.activities.${activityIndex}.examDetails`
                      );

                      return (
                        <div key={activityIndex} className="mt-3 ml-4 border-l-2 pl-4 border-gray-300">
                          <div className="flex items-center gap-2 mb-2">
                            <input
                              type="text"
                              className="border border-gray-300 rounded p-2 w-64"
                              placeholder="Activity Name"
                              {...register(
                                `weeks.${weekIndex}.activities.${activityIndex}.activityName`
                              )}
                            />
                            <select
                              className="border border-gray-300 rounded p-2 w-52"
                              {...register(
                                `weeks.${weekIndex}.activities.${activityIndex}.activityType`
                              )}
                            >
                              <option value="">Select Activity Type</option>
                              <option value="Document">Reading</option>
                              <option value="Video">Video</option>
                              <option value="Exam">Exam</option>
                            </select>

                            {activityType === "Document" ||
                            activityType === "Video" ? (
                              <div className="flex flex-col">
                                <input
                                  type="file"
                                  className="border border-gray-300 rounded p-2"
                                  accept={
                                    activityType === "Video"
                                      ? "video/*"
                                      : activityType === "Document"
                                      ? ".pdf,.doc,.docx,.txt"
                                      : "image/*"
                                  }
                                  onChange={(event) => {
                                    const file = event.target.files[0];
                                    if (file) {
                                      const activities = watch(
                                        `weeks.${weekIndex}.activities`
                                      );
                                      activities[activityIndex].file = [file];
                                      setValue(
                                        `weeks.${weekIndex}.activities`,
                                        [...activities]
                                      );
                                      
                                      // Also update the activityFiles state
                                      setActivityFiles(prev => ({
                                        ...prev,
                                        [fileKey]: URL.createObjectURL(file)
                                      }));
                                    }
                                  }}
                                />

                                {/* Display existing file link if available */}
                                {(filePath && typeof filePath === 'string' && filePath !== "") || existingFile ? (
                                  <a
                                    href={existingFile || filePath}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-500 underline mt-1 text-sm"
                                  >
                                    {activityType === "Document" ? "View Document" : "View Video"}
                                  </a>
                                ) : null}
                              </div>
                            ) : activityType === "Exam" ? (
                              <div className="flex flex-col">
                                {/* Register hidden field for file to ensure it's always present in form data */}
                                <input
                                  type="hidden"
                                  {...register(
                                    `weeks.${weekIndex}.activities.${activityIndex}.file`
                                  )}
                                  value=""
                                />
                                <button
                                  type="button"
                                  className="bg-[#241763] text-white px-3 py-1 rounded flex items-center"
                                  onClick={() => openExamModal(weekIndex, activityIndex)}
                                >
                                  <Plus className="mr-1" size={16} />
                                  {examDetails ? "Edit Exam" : "Add Exam"}
                                </button>
                                {examDetails && (
                                  <div className="mt-1 text-sm text-green-600">
                                    Exam added: {examDetails.title}
                                  </div>
                                )}
                              </div>
                            ) : null}

                            <button
                              type="button"
                              className="bg-red-500 text-white rounded p-2"
                              onClick={() =>
                                removeActivity(weekIndex, activityIndex)
                              }
                            >
                              &#x2715;
                            </button>
                          </div>
                          <div>
                            <textarea
                              className="border border-gray-300 rounded p-2 w-full"
                              placeholder="Activity Description"
                              rows="2"
                              {...register(
                                `weeks.${weekIndex}.activities.${activityIndex}.description`
                              )}
                            />
                          </div>
                        </div>
                      );
                    }
                  )}
                  {(!watch(`weeks.${weekIndex}.activities`) || 
                    watch(`weeks.${weekIndex}.activities`).length === 0) && (
                    <div className="mt-2 text-gray-500 text-center py-4">
                      No activities added yet. Click "Add Activity" to create one.
                    </div>
                  )}
                </div>
              ))}
              {weekFields.length === 0 && (
                <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <p className="text-gray-500">No weeks added yet. Click "Add Week" to start planning your course.</p>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-end mt-6">
          <button
            type="submit"
            className="bg-[#241763] text-white py-2 px-6 rounded hover:bg-[#352584] transition duration-200 ease-in-out"
          >
            {isEdit ? "Update Course" : "Create Course"}
          </button>
        </div>
        
        {examModal && (
          <Modal
            open={examModal}
            onCancel={() => setExamModal(false)}
            footer={null}
            width={800}
          >
            <AddExamModal
              onRequestClose={() => setExamModal(false)}
              setExamData={handleExamData}
              isCourse={true}
              currentExamData={getCurrentExamDetails()}
            />
          </Modal>
        )}
      </form>
    </>
  );
};
export default CoursesModal;
